import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import IsometricTwoWayTexting from '../components/IsometricTwoWayTexting';
import { MessageSquare, Bell, Users, Smartphone, CheckCircle, ArrowLeftRight, Clock } from 'lucide-react';
import { useSalesTracking } from '../hooks/useSalesTracking';
import { usePageTitle } from '../hooks/usePageTitle';
import { trackProductPageView } from '../utils/analytics';
import { APP_NAME } from '../config/branding';

const TwoWayTexting: React.FC = () => {
  usePageTitle('2-Way SMS Texting for Veterinary Practices');

  useSalesTracking();

  useEffect(() => {
    trackProductPageView('two_way_texting');
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-dark-bg-primary">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-teal-100 text-teal-600 text-sm font-medium rounded-full">
                2-Way Texting
              </span>
            </div>

            <h1 className="text-5xl font-bold text-gray-900 dark:text-dark-text-primary mb-6">
              Unlimited two-way texting
            </h1>

            <p className="text-xl text-gray-600 dark:text-dark-text-secondary mb-8">
              Clients appreciate the convenience of texting your practice directly, and your team responds from a shared inbox.
            </p>

            <div className="flex gap-4 mb-8">
              <Link
                to="/pricing"
                className="bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
              >
                See pricing
              </Link>
              <Link
                to="/contact"
                className="bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Request demo →
              </Link>
            </div>
          </div>

          <IsometricTwoWayTexting />
        </div>

        {/* Feature Highlights */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <ArrowLeftRight className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Unlimited two-way texting</h3>
              <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Clients text your practice directly, and your team responds from a shared inbox.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary mb-1">End Phone Tag</h3>
              <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Respond to questions, refill requests, and appointment needs instantly without picking up the phone.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Team Inbox</h3>
              <p className="text-sm text-gray-600 dark:text-dark-text-secondary">All staff can see and respond to client messages from the shared dashboard.</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-gray-50 dark:bg-dark-bg-secondary py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary mb-12 text-center">Simple, Professional Communication</h2>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Get Your Practice Number</h3>
                  <p className="text-gray-600 dark:text-dark-text-secondary">Each practice gets a dedicated phone number for texting. Clients text this number, not personal phones.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Clients Text You</h3>
                  <p className="text-gray-600 dark:text-dark-text-secondary">Pet owners text your practice with questions or photos instead of calling and waiting on hold.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary mb-1">Team Responds via Dashboard</h3>
                  <p className="text-gray-600 dark:text-dark-text-secondary">Incoming texts appear in your shared inbox. Any staff member can reply directly from their computer.</p>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <img
                src="/text.png"
                alt="Phone notification showing SMS message"
                className="w-64 md:w-80 rounded-[2.5rem] shadow-2xl border-8 border-gray-900"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      < div className="py-16" >
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary mb-8 text-center">Why 2-Way Texting Works</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">98% open rate</h3>
                <p className="text-gray-600 dark:text-dark-text-secondary">Text messages are read almost immediately—unlike emails that get buried.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">Reduce no-shows</h3>
                <p className="text-gray-600 dark:text-dark-text-secondary">Appointment reminders with easy confirmation cut no-shows dramatically.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">Keep personal phones private</h3>
                <p className="text-gray-600 dark:text-dark-text-secondary">Staff never need to share their personal numbers with clients.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">Conversation history</h3>
                <p className="text-gray-600 dark:text-dark-text-secondary">Every message is saved and tied to the client record for reference.</p>
              </div>
            </div>
          </div>
        </div>
      </div >

      {/* CTA Section */}
      <div className="py-16 bg-teal-50 dark:bg-dark-bg-secondary">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
            Start texting your clients today
          </h2>
          <p className="text-xl text-gray-600 dark:text-dark-text-secondary mb-8">
            Join practices that have improved client communication and reduced no-shows with 2-way texting.
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/pricing"
              className="bg-teal-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
            >
              See Pricing
            </Link>
            <Link
              to="/contact"
              className="bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary px-8 py-3 rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors border border-gray-200 dark:border-dark-border"
            >
              Talk to sales
            </Link>
          </div>
        </div>
      </div>
    </div >
  );
};

export default TwoWayTexting;
